import { createClient } from 'contentful';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import isPlainObject from 'lodash-es/isPlainObject';
import isArray from 'lodash-es/isArray';

var clients = {};

var environments = {
  preview: {
    space: 'c9fg5kdzsxhp',
    accessToken: '218118f3c489a63dfc305480b6529b36efc0c0a12da7bfdea85fd69ed23271a6',
    host: 'preview.contentful.com'
  },
  production: {
    space: 'c9fg5kdzsxhp',
    accessToken: '68a7a257e2736dfb2dbed3e2e4596970b50d0e3561aaaf6b646a8e700e19f109',
    host: 'cdn.contentful.com'
  }
};

/**
 * @memberof module:contentful
 * @param {string} environmentName The name of the contentful environment. Valid
 * options are `preview` or `production`. NOTE: This is a contentful environment
 * not a normal updater environment.
 *
 * @returns {Object} A contentful client OR null if something goes wrong during
 * client instantiation.
 *
 * @description Returns a contentful client that points at the given
 * environment. This client is a singleton and will only be created once per
 * call. Subsequent calls return a cached version. See
 * [here](https://contentful.github.io/contentful.js/contentful/latest/ContentfulClientAPI.html)
 * for information about how to use the contentful client.
 */
function getClient(cmsEnvironmentName) {
  if (clients[cmsEnvironmentName]) {
    return clients[cmsEnvironmentName];
  }

  try {
    return createClient(environments[cmsEnvironmentName]);
  } catch (e) {
    console.log(e);
    return null;
  }
}

var classes = {
    "class": "mc3bdf288b_class"
};
var styles = ".mc3bdf288b_class h1 {\n  font-size: 2em;\n  line-height: 1.5;\n}\n\n.mc3bdf288b_class h2 {\n  font-size: 1.5em;\n  line-height: 1.5;\n}\n\n.mc3bdf288b_class h3 {\n  font-size: 1em;\n  line-height: 1.5;\n}\n\n.mc3bdf288b_class h4 {\n  font-size: 0.85em;\n  color: #666667;\n}\n\n.mc3bdf288b_class b,\n.mc3bdf288b_class strong {\n  font-weight: 500;\n}\n\n.mc3bdf288b_class p {\n  margin-top: 0.5em;\n}\n";

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var styleNode = null;

var createStyleNode = function createStyleNode(styles$$1) {
  var styleNode = document.createElement('style');
  styleNode.appendChild(document.createTextNode(styles$$1));
  return styleNode;
};

var addStyles = function addStyles(styles$$1, shadowParent) {
  var styleNodeUsed = styleNode;
  if (!shadowParent && !styleNode) {
    styleNodeUsed = styleNode = createStyleNode(styles$$1);
    document.head.insertBefore(styleNode, document.head.firstElementChild);
  } else if (shadowParent && shadowParent.shadowRoot) {
    styleNodeUsed = createStyleNode(styles$$1);
    shadowParent.shadowRoot.insertBefore(styleNodeUsed, shadowParent.shadowRoot.firstElementChild);
  }
  return styleNodeUsed;
};

/**
 * @memberof module:contentful
 * @param {Object} fulltext A contentful full text document.
 * @param {Object} options A list of options
 * @param {Object} options.shadowParent The parent element whos shadowRoot the
 * provided styles should be prepended to.
 * @param {function} options.templateFn A function which when given an
 * htmlstring with template tags in it returns and htmlstring with the template
 * tags interpolated.
 * @param {function} options.renderOptions An options object passed to the full
 * text renderer. See documentation
 * [here](https://github.com/contentful/rich-text/tree/master/packages/rich-text-html-renderer#usage)
 * @returns {Object} object An object containing `div`(the element generated)
 * and `styles`(the style node containing the styles)
 *
 * @description
 * Generates a div within which a compiled full text document will be rendered.
 * In addition it prepends styles required for proper display to either the
 * document's head or the provided shadowParent's shadowRoot. Prepending is done
 * in order to allow for easy overriding of the included styles. Styles are only
 * prepended once upon call of the function. Rerendering a shadowRoot, or
 * overwriting the document head will wipe these styles out. `styles` is
 * provided on the return value for independent usage if necessary.
 *
 * @example
 * import {createTextNode, getClient} from '@updater/ui-contentful';
 *
 * const id = 'somecontentfulid';
 * const client = getClient('preview');
 * if (client) {
 *   const entity = client.getEntity(id);
 *   const {div, styles} = createFullTextNode(entity.fields.fullText);
 *
 *   document.body.appendChild(div);
 * }
 */
function createFullTextNode(fulltext) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      shadowParent = _ref.shadowParent,
      templateFn = _ref.templateFn,
      renderOptions = _ref.renderOptions;

  var styleNodeUsed = addStyles(styles, shadowParent);
  if ((typeof fulltext === 'undefined' ? 'undefined' : _typeof(fulltext)) !== 'object') {
    console.warn('Invalid value passed to createTextNode: ' + fullText);
    return {
      div: document.createComment('Invalid value passed to createTextNode'),
      styles: styleNodeUsed
    };
  }

  var div = document.createElement('div');
  var htmlString = documentToHtmlString(fulltext, renderOptions);
  if (typeof templateFn === 'function') {
    htmlString = templateFn(htmlString);
    if (typeof htmlString !== 'string') {
      console.warn('templateFn returned an invalid value: ' + htmlString);
      return {
        div: document.createComment('Invalid templateFn'),
        styles: styleNodeUsed
      };
    }
  }

  div.innerHTML = htmlString;
  div.classList.add(classes.class);
  return { div: div, styles: styleNodeUsed };
}

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var promote = function promote(keyToPromote) {
  return function (obj) {
    var promoteDeep = promote(keyToPromote);
    if (isArray(obj)) {
      return obj.map(function (x) {
        return promoteDeep(x);
      });
    }

    var keys = Object.keys(obj);
    return keys.reduce(function (acc, key) {
      var val = obj[key];
      if (isPlainObject(val)) {
        if (val[keyToPromote]) {
          return _extends({}, acc, _defineProperty({}, key, promoteDeep(val[keyToPromote])));
        } else {
          return _extends({}, acc, _defineProperty({}, key, promoteDeep(val)));
        }
      } else if (isArray(val)) {
        return _extends({}, acc, _defineProperty({}, key, val.map(function (x) {
          return promoteDeep(x);
        })));
      }

      return acc;
    }, obj);
  };
};

var removeENLocalization = promote('en-US');

/**
 * @memberof module:contentful
 * @param {string} environmentName The name of the contentful environment. Valid
 * options are `preview` or `production`. NOTE: This is a contentful environment
 * not a normal updater environment.
 * @param {Array} entryIds Which entrys to save
 * @returns {Promise} space A promise that's resolved with an object containing all of the space data that's
 * been requested or an empty object if no data is found.
 *
 * @description Syncs the requested entries from the requested environment
 */
function sync(environmentName, entryIds) {
  var client = getClient(environmentName);
  if (!client) {
    console.error('Failed to create a client for ', environmentName);
  }

  return client.sync({ initial: true, resolveLinks: true }).then(function (r) {
    var state = r.entries.reduce(function (state, entry) {
      if (entryIds.includes(entry.sys.id)) {
        return _extends({}, state, _defineProperty({}, entry.sys.id, entry));
      }

      return state;
    }, {});
    return removeENLocalization(state);
  }).catch(function (e) {
    console.error(e);
    return {};
  });
}

/**
 * Helpers for working with Updater's CMS.
 *
 * ### Differences between `cjs` and `es` builds:
 *
 * - `syncToDisk` is not included in the `es` build due to the fact that it will
 *   not function in the browser.
 * - `createFullTextNode` is not included in the `cjs` build due to the fact
 *   that it will not function in node.
 *
 * As a result if you are bundling this for a browser make sure to use the
 * `module` build instead of the `main` build. If your bundler doesn't
 * automatically prefer `module` you can import it with:
 *
 * ```javascript
 * import ... from '@updater/ui-contentful/index.module.js'
 * ```
 *
 * @module contentful
 */

export { createFullTextNode, getClient, sync };
